import { z } from 'zod';
import { pipe, Option, Array as A } from 'effect';

// ── entity schemas ────────────────────────────────────────────────────────────

/** A field of a partially matched line whose value disagrees with the document. */
export const DeviatedCellSchema = z.object({
  doc_value: z.string(),
});

const MatchSchema = z.object({
  status: z.literal('match'),
  line: z.number(),
});

const PartialMatchSchema = z.object({
  status: z.literal('partial_match'),
  line: z.number(),
  // A malformed cells section costs the hints, not the line. `catch` and not `default`:
  // `default` only fills the field in when it is absent, so a present-but-off-contract
  // section fails this variant and the union's permissive one then swallows the line and
  // demotes it to `unknown`, losing the row highlight it had earned.
  cells: z.record(z.string(), DeviatedCellSchema).catch({}),
});

/**
 * A match state this client does not know. Without this variant one unrecognised
 * status would fail the whole payload and drop matching for the entire document;
 * with it, only that line loses its highlight. The tag is what tells a reader
 * that asking about deviated cells is not meaningful here.
 */
const UnknownMatchSchema = z
  .object({ status: z.string(), line: z.number() })
  .transform(({ line }) => ({ status: 'unknown' as const, line }));

export const MatchedLineResultSchema = z.union([MatchSchema, PartialMatchSchema, UnknownMatchSchema]);

/** Keyed by JOURNAL-ENTRY line number; `line` inside a result is the DOCUMENT table row. */
export const MatchedLinesSchema = z.record(z.string(), z.array(MatchedLineResultSchema));

export type DeviatedCell = z.output<typeof DeviatedCellSchema>;
export type MatchedLineResult = z.output<typeof MatchedLineResultSchema>;
export type MatchedLines = z.output<typeof MatchedLinesSchema>;

/** Off-contract payloads degrade to no matching rather than breaking the journal entry. */
export function parseMatchedLines(raw: unknown): MatchedLines {
  return MatchedLinesSchema.safeParse(raw ?? {}).data ?? {};
}

// ── JE cell option lists ─────────────────────────────────────────────────────

const ErpId = z.union([z.string(), z.number()]).transform(String);

// looseObject, not object: responses carry keys we don't read, and stripping
// them would drop behaviour the JE cells rely on.
export const JournalCellOptionSchema = z.looseObject({
  value: ErpId,
  display: z.string(),
});

export const JournalCellOptionsQuerySchema = z.object({
  documentID: z.string(),
  cellName: z.string(),
  search: z.string().optional(),
  // Required on purpose: omitted, it comes back short, and a silently
  // truncated list is the kind of bug nobody reports.
  pageSize: z.number(),
  pageToken: z.string().nullable().optional(),
});

export const JournalCellOptionsResponseSchema = z.object({
  list: z.array(JournalCellOptionSchema),
  // Normalised to string|null so it can be sent straight back as the next query.
  pageToken: z
    .union([z.string(), z.number()])
    .nullish()
    .transform((v) => (v == null ? null : String(v))),
});

// An empty object means the document has no vendor — hence both fields optional.
export const DocumentVendorQuerySchema = z.object({ documentID: z.string() });
export const DocumentVendorResponseSchema = z.object({ value: ErpId, display: z.string() }).partial();

export type JournalCellOption = z.output<typeof JournalCellOptionSchema>;
export type DocumentVendor = z.output<typeof DocumentVendorResponseSchema>;

// ── JE index form ────────────────────────────────────────────────────────────

// What the read sends. Carried across from the legacy call verbatim.
export const JEIndexFormQuerySchema = z.object({
  field: z.string(),
  value: z.string(),
  document_id: z.string(),
});

// One field of the index-list create/edit form, as the read returns it. Every
// field carries these keys; a `list` field carries `options` on top.
const JEIndexFormFieldWireSchema = z.object({
  name: z.string(),
  label: z.string(),
  // Only 'text', 'list' and 'boolean' are rendered. Left as a plain string
  // rather than an enum on purpose: an unrecognised type has to keep passing
  // its value through untouched, which is what happens today, instead of
  // failing the whole read and leaving the dialog with nothing to show.
  type: z.string(),
  mandatory: z.boolean(),
  // Null is observed, not defensive: `tax_group` arrives null, and so does the
  // base-transaction-type list on the create path. A number arrives too.
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  // `list` only. Key = the value that goes back on submit, map value = the text
  // the user is meant to read.
  options: z.record(z.string(), z.unknown()).optional(),
});

// An option's label falls back to its key when the display text is missing or
// blank, so a sparse map renders readable rows instead of empty ones.
const optionLabel = (key: string, label: unknown): string =>
  pipe(
    Option.fromNullable(label),
    Option.map(String),
    Option.filter((text) => text.length > 0),
    Option.getOrElse(() => key),
  );

export const JEIndexFormSchema = z.array(JEIndexFormFieldWireSchema);

type JEIndexFormFieldWire = z.infer<typeof JEIndexFormFieldWireSchema>;

// The shape the form consumes: options mapped to a list, in the order they
// arrived.
export type JEIndexFormField = Omit<JEIndexFormFieldWire, 'options'> & {
  options: { value: string; label: string }[];
};

const toJEIndexFormField = (field: unknown): JEIndexFormField => {
  const { options, ...rest } = field as JEIndexFormFieldWire;

  return {
    ...rest,
    options: pipe(
      Object.entries(options ?? {}),
      A.map(([value, label]) => ({ value, label: optionLabel(value, label) })),
    ),
  };
};

// Deliberately not a `.transform()` on the schema above. On a response-schema
// mismatch the request layer reports to Sentry and returns the body raw in
// production, so a reshaping schema yields a value its own type rules out —
// `options` as a record where the type promises a list — and one field missing
// one key poisons the whole array, healthy siblings included. Mapping here runs
// on whatever actually came back, so a drifted field only loses the key that
// drifted. Both facts probed, not assumed.
//
// The guard takes `unknown`, not `unknown[]`, for the same reason: that raw
// body is not always an array. It has been a string and an object, and both
// crashed an `unknown[]` signature. `A.ensure` is the trap that looks like the
// answer here — it wraps a non-array instead of discarding it, so a 500's body
// would arrive as one bogus field.
export const toJEIndexFormFields = (fields: unknown): JEIndexFormField[] =>
  pipe(
    A.isArray(fields) ? fields : [],
    // The mapper destructures each member, so a null entry throws where the note
    // above promises it would only cost that entry.
    A.filter((field) => typeof field === 'object' && field !== null),
    A.map(toJEIndexFormField),
  );
